'use client';

import { useCallback, useEffect, useState } from 'react';

import { FormSubmissionModal } from '@/components/forms/FormSubmissionModal/FormSubmissionModal';
import { BookingWidgetClient } from '@/features/booking/components/BookingWidgetClient';
import { useBookingFlowStore } from '@/features/booking/store/useBookingFlowStore';
import { mapDoctorToBookingDoctor } from '@/features/booking/utils/mapDoctorToBookingDoctor';
import type { Doctor } from '@/types/doctor';
import { getClinicsList } from '@/data/clinics';
import {
  serviceEnquiryDefaultValues,
  serviceEnquiryFields,
  serviceEnquirySchema,
} from '@/features/enquiries/forms/serviceEnquirySchema';
import { getDoctorsListWithBookingFields } from '@/features/specialists/data/doctorsData';

import MostPopularSection from '../MostPopularSection/MostPopularSection';
import PackagesClientWrapper from '../PackagesClientWrapper';
import type {
  BookPayload,
  EnquirePayload,
  FlatCategoriesData,
  FlatMostPopularData,
} from '../../types';

interface HealthPackagesClientSectionsProps {
  mostPopular: FlatMostPopularData;
  packagesData: FlatCategoriesData;
}

export default function HealthPackagesClientSections({
  mostPopular,
  packagesData,
}: HealthPackagesClientSectionsProps) {
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [enquirePayload, setEnquirePayload] = useState<EnquirePayload>({
    category: '',
  });
  const [bookingWidgetOpen, setBookingWidgetOpen] = useState(false);

  const setSelectedDoctor = useBookingFlowStore((s) => s.setSelectedDoctor);
  const setCurrentStep = useBookingFlowStore((s) => s.setCurrentStep);
  const setNotes = useBookingFlowStore((s) => s.setNotes);
  const setDepositFee = useBookingFlowStore((s) => s.setDepositFee);
  const setPackagePrice = useBookingFlowStore((s) => s.setPackagePrice);
  const setShowModal = useBookingFlowStore((s) => s.setShowModal);
  const showModal = useBookingFlowStore((s) => s.showModal);

  // Reset local flag when widget is closed via store
  useEffect(() => {
    if (!showModal && bookingWidgetOpen) {
      setBookingWidgetOpen(false);
    }
  }, [showModal, bookingWidgetOpen]);

  const handleEnquire = useCallback((payload: EnquirePayload) => {
    setEnquirePayload(payload);
    setHasBeenOpened(true);
    setEnquiryOpen(true);
  }, []);

  const handleBook = useCallback(
    async (payload: BookPayload) => {
      const apiId = payload.apiIds?.[0];
      if (!apiId) {
        // No apiIds — fall back to enquiry
        handleEnquire(payload);
        return;
      }

      const doctors = await getDoctorsListWithBookingFields();
      const doctor = doctors.find((d) => d.apiId === apiId);

      if (!doctor) {
        // Step 2: try clinic list (synchronous — clinics are static imports)
        const clinics = getClinicsList();
        const clinic = clinics.find((c) => c.apiId === apiId);

        if (clinic) {
          const bookingDoctor = mapDoctorToBookingDoctor(clinic);
          setSelectedDoctor(bookingDoctor);
          setCurrentStep(1);
          setNotes(`${payload.title} - ${payload.category}`);
          if (payload.depositFee !== null) {
            setDepositFee(payload.depositFee);
          }
          if (payload.price !== null) {
            setPackagePrice(Number(payload.price));
          }
          setShowModal(true);
          setBookingWidgetOpen(true);
          return;
        }

        // Step 3: neither doctor nor clinic matched — fall back to enquiry
        console.warn(
          `HealthPackages: no doctor or clinic found for apiId "${apiId}" (package: "${payload.title}"). Falling back to enquiry.`,
        );
        handleEnquire(payload);
        return;
      }

      const bookingDoctor = mapDoctorToBookingDoctor(doctor as Doctor);
      setSelectedDoctor(bookingDoctor);
      setCurrentStep(1);
      setNotes(`${payload.title} - ${payload.category}`);
      if (payload.depositFee !== null) {
        setDepositFee(payload.depositFee);
      }
      if (payload.price !== null) {
        setPackagePrice(Number(payload.price));
      }
      setShowModal(true);
      setBookingWidgetOpen(true);
    },
    [
      handleEnquire,
      setSelectedDoctor,
      setCurrentStep,
      setNotes,
      setDepositFee,
      setPackagePrice,
      setShowModal,
    ],
  );

  return (
    <>
      <MostPopularSection
        data={mostPopular}
        onEnquire={handleEnquire}
        onBook={handleBook}
      />
      <PackagesClientWrapper
        data={packagesData}
        onEnquire={handleEnquire}
        onBook={handleBook}
      />
      {hasBeenOpened && (
        <FormSubmissionModal
          open={enquiryOpen}
          onOpenChange={setEnquiryOpen}
          formId="serviceEnquiry"
          schema={serviceEnquirySchema}
          fields={serviceEnquiryFields}
          defaultValues={serviceEnquiryDefaultValues}
          title="Enquire about this health package"
          description={`You are enquiring about: ${enquirePayload.title || enquirePayload.category}`}
          submitLabel="Send enquiry"
          metadata={{
            source: enquirePayload.source || 'health_packages_most_popular',
            pageType: 'health-package',
            serviceName: enquirePayload.title || enquirePayload.category,
          }}
        />
      )}
      {bookingWidgetOpen && <BookingWidgetClient />}
    </>
  );
}
